import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown, Menu, X, Sparkles } from "lucide-react";
import { useLang, LANGS } from "../i18n/LanguageContext";
import { SERVICE_SLUGS, waLink } from "../config";

const LangSwitcher = ({ className = "", onPick, idPrefix = "lang" }) => {
  const { lang, setLang } = useLang();
  return (
    <div className={`flex items-center gap-1 ${className}`} data-testid={`${idPrefix}-switcher`}>
      {LANGS.map((l, i) => (
        <span key={l.code} className="flex items-center">
          <button
            data-testid={`${idPrefix}-${l.code}`}
            onClick={() => {
              setLang(l.code);
              onPick && onPick();
            }}
            className={`px-1 text-[12px] font-semibold tracking-[0.14em] transition-colors duration-300 ${
              lang === l.code ? "text-rosedeep" : "text-ink/45 hover:text-ink"
            }`}
            aria-label={l.name}
          >
            {l.label}
          </button>
          {i < LANGS.length - 1 && <span className="text-gold/50 text-[10px]">|</span>}
        </span>
      ))}
    </div>
  );
};

export const Header = () => {
  const { t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [ddOpen, setDdOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setDdOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => (document.body.style.overflow = "");
  }, [mobileOpen]);

  const links = [
    { to: "/", label: t.nav.home },
    { to: "/sobre", label: t.nav.about },
    { to: "/transformacoes", label: t.nav.transformations },
    { to: "/galeria", label: t.nav.gallery },
    { to: "/contato", label: t.nav.contact },
  ];

  return (
    <>
      <header
        data-testid="main-header"
        className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
          scrolled
            ? "bg-cream/95 backdrop-blur-md shadow-[0_10px_40px_-18px_rgba(142,75,92,0.25)] border-b border-gold/15"
            : "bg-cream/80 backdrop-blur-sm"
        }`}
      >
        <div
          className={`mx-auto flex max-w-7xl items-center justify-between px-5 sm:px-8 transition-all duration-500 ${
            scrolled ? "py-2.5" : "py-4"
          }`}
        >
          <Link to="/" className="flex items-center gap-3" data-testid="header-logo" aria-label="Bloom Beauty Concept Hair">
            <img
              src="/assets/logo-mark.png"
              alt="Bloom Beauty Concept Hair"
              className={`rounded-full transition-all duration-500 ${scrolled ? "w-9 h-9" : "w-11 h-11"}`}
            />
            <span className="hidden sm:block leading-none">
              <span className="block font-serif text-lg font-semibold tracking-[0.18em] text-ink">BLOOM</span>
              <span className="block text-[9px] font-semibold tracking-[0.3em] text-rosedeep uppercase">
                Beauty Concept Hair
              </span>
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-7" aria-label="Principal">
            <NavLink to="/" end className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`} data-testid="nav-home">
              {t.nav.home}
            </NavLink>
            <NavLink to="/sobre" className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`} data-testid="nav-about">
              {t.nav.about}
            </NavLink>

            <div
              className="relative"
              onMouseEnter={() => setDdOpen(true)}
              onMouseLeave={() => setDdOpen(false)}
            >
              <button
                data-testid="nav-services"
                aria-expanded={ddOpen}
                aria-haspopup="true"
                onClick={() => setDdOpen((v) => !v)}
                className={`nav-link flex items-center gap-1 ${
                  location.pathname.startsWith("/servicos") ? "active" : ""
                }`}
              >
                {t.nav.services}
                <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-300 ${ddOpen ? "rotate-180" : ""}`} />
              </button>
              <AnimatePresence>
                {ddOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 12, scale: 0.98 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 8, scale: 0.98 }}
                    transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
                    className="absolute left-1/2 top-full z-50 w-[34rem] -translate-x-1/2 pt-4"
                    data-testid="services-dropdown"
                  >
                    <div className="rounded-2xl border border-gold/20 bg-white/95 p-3 shadow-[0_30px_60px_-20px_rgba(142,75,92,0.35)] backdrop-blur-xl">
                      <div className="grid grid-cols-2 gap-1">
                        {SERVICE_SLUGS.map((slug) => (
                          <Link
                            key={slug}
                            to={`/servicos/${slug}`}
                            data-testid={`dd-${slug}`}
                            className="group rounded-xl px-4 py-3 transition-colors duration-300 hover:bg-blush/50"
                          >
                            <span className="block text-[13px] font-semibold text-ink group-hover:text-rosedeep transition-colors">
                              {t.services[slug].name}
                            </span>
                            <span className="mt-0.5 block text-[11px] leading-snug text-ink/50">
                              {t.services[slug].short}
                            </span>
                          </Link>
                        ))}
                        <Link
                          to="/avaliacao"
                          data-testid="dd-avaliacao"
                          className="group rounded-xl px-4 py-3 bg-gradient-to-br from-blush/60 to-transparent border border-gold/20 transition-colors duration-300 hover:border-gold/50"
                        >
                          <span className="flex items-center gap-1.5 text-[13px] font-semibold text-rosedeep">
                            <Sparkles className="w-3.5 h-3.5 text-gold" />
                            {t.services.avaliacao.name}
                          </span>
                          <span className="mt-0.5 block text-[11px] leading-snug text-ink/50">
                            {t.services.avaliacao.short}
                          </span>
                        </Link>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {links.slice(2).map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
                data-testid={`nav-${l.to.replace("/", "")}`}
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-5">
            <LangSwitcher />
            <a
              href={waLink(t.waEval)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="header-book-btn"
              className="rounded-full bg-rosedeep px-5 py-2.5 text-[12px] font-bold uppercase tracking-[0.14em] text-white shadow-[0_10px_25px_-10px_rgba(168,94,112,0.7)] transition-all duration-300 hover:bg-ink hover:shadow-[0_14px_30px_-10px_rgba(43,27,32,0.5)]"
            >
              {t.nav.book}
            </a>
          </div>

          <button
            className="lg:hidden p-2 text-ink"
            onClick={() => setMobileOpen(true)}
            aria-label={t.nav.menu}
            data-testid="mobile-menu-open"
          >
            <Menu className="w-6 h-6" />
          </button>
        </div>
      </header>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[85] flex flex-col overflow-y-auto bg-mauve"
            data-testid="mobile-menu"
          >
            <div className="flex items-center justify-between px-5 py-4">
              <img src="/assets/logo-full.png" alt="Bloom Beauty Concept Hair" className="h-12 w-auto rounded-lg" />
              <button
                onClick={() => setMobileOpen(false)}
                aria-label={t.nav.close}
                data-testid="mobile-menu-close"
                className="p-2 text-cream"
              >
                <X className="w-7 h-7" />
              </button>
            </div>
            <nav className="flex flex-1 flex-col gap-1 px-8 py-6" aria-label="Menu móvel">
              {[
                { to: "/", label: t.nav.home, id: "m-home" },
                { to: "/sobre", label: t.nav.about, id: "m-about" },
                { to: "/servicos", label: t.nav.services, id: "m-services" },
                { to: "/transformacoes", label: t.nav.transformations, id: "m-transformations" },
                { to: "/galeria", label: t.nav.gallery, id: "m-gallery" },
                { to: "/contato", label: t.nav.contact, id: "m-contact" },
                { to: "/avaliacao", label: t.services.avaliacao.name, id: "m-avaliacao" },
              ].map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, x: -18 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.06 * i, duration: 0.4 }}
                >
                  <Link
                    to={l.to}
                    data-testid={l.id}
                    className="block border-b border-cream/15 py-4 font-serif text-3xl font-medium text-cream"
                  >
                    {l.label}
                  </Link>
                </motion.div>
              ))}
            </nav>
            <div className="px-8 pb-10">
              <LangSwitcher idPrefix="lang-m" className="mb-5 [&_button]:!text-cream/80 [&_button]:text-sm" onPick={() => {}} />
              <a
                href={waLink(t.waEval)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="mobile-book-btn"
                className="block rounded-full bg-cream px-6 py-4 text-center text-sm font-bold uppercase tracking-[0.16em] text-rosedeep"
              >
                {t.nav.book}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Header;
